import React, { useState } from 'react';
import { AddressFormData, Address, ComponentValidation, ComponentValidationLevel } from './types';
import { FormErrors } from '../../../types';
import { addressValidatorApi, AddressValidationResponse, InferredComponents } from './services/addressValidatorApi';

interface AddressFormSectionProps {
  addressData: AddressFormData;
  errors: FormErrors;
  onChange: (field: keyof AddressFormData, value: string) => void;
  verified?: boolean;
  onValidated?: (componentValidation: ComponentValidation) => void; // Callback when user selects a validated address
}

const AddressFormSection: React.FC<AddressFormSectionProps> = ({
  addressData,
  errors,
  onChange,
  verified = false,
  onValidated
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [componentValidation, setComponentValidation] = useState<ComponentValidation | null>(null);
  const [inferredComponents, setInferredComponents] = useState<InferredComponents | null>(null);
  const [validatedAddress, setValidatedAddress] = useState<Address | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  const handleAddressChange = (field: keyof AddressFormData, value: string) => {
    onChange(field, value);
    // Clear validation when user changes address fields
    setComponentValidation(null);
    setInferredComponents(null);
    setShowBanner(false);
    setValidatedAddress(null);
  };

  const handleVerifyAddress = async () => {
    // Require at least some address information to validate
    // User can enter partial addresses (e.g., just street + city, or just zip)
    const hasStreet = addressData.street1?.trim();
    const hasCity = addressData.city?.trim();
    const hasState = addressData.state?.trim();
    const hasZip = addressData.zip?.trim();
    const hasCountry = addressData.country?.trim();
    
    // Require at least: (street OR city) AND (state OR zip OR country)
    // This allows various combinations like:
    // - street + city + state
    // - street + zip
    // - city + state
    // - zip + country
    const hasLocation = hasStreet || hasCity;
    const hasRegion = hasState || hasZip || hasCountry;
    
    if (!hasLocation || !hasRegion) {
      alert('Please enter at least some address information (e.g., street address and city, or zip code) before verifying.');
      return;
    }

    setIsValidating(true);

    try {
      // Convert AddressFormData to Address format for API
      // Use empty strings for missing fields - Google API can handle partial addresses
      const addressToValidate: Address = {
        id: 0,
        street1: addressData.street1?.trim() || '',
        street2: addressData.street2?.trim() || '',
        unit: addressData.unit?.trim() || '',
        city: addressData.city?.trim() || '',
        state: addressData.state?.trim() || '',
        zip: addressData.zip?.trim() || '',
        country: addressData.country?.trim() || 'US', // Default to US if not provided
        verified: false,
        dateCreated: '',
        dateUpdated: '',
        whoUpdated: ''
      };

      const validatedResponses = await addressValidatorApi.validateAddress(addressToValidate);
      
      if (validatedResponses && validatedResponses.length > 0) {
        const firstResponse = validatedResponses[0];
        
        // Always show banner with validated address
        // Use inferred address if available, otherwise use the validated address
        const addressToShow = firstResponse.hasInferredComponents && firstResponse.inferredAddress
          ? firstResponse.inferredAddress
          : firstResponse.address;
        
        setValidatedAddress(addressToShow);
        setShowBanner(true);
        setComponentValidation(firstResponse.componentValidation);
        setInferredComponents(firstResponse.inferredComponents || null);
      } else {
        // No high probability matches found - provide more helpful message
        alert('No high probability address matches were found. Try entering more address details or check for typos.');
        setComponentValidation(null);
        setInferredComponents(null);
        setShowBanner(false);
        setValidatedAddress(null);
      }
    } catch (error: any) {
      console.error('Error validating address:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to validate address. Please try again.';
      alert(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  const handleUseValidatedAddress = () => {
    if (validatedAddress) {
      onChange('street1', validatedAddress.street1 || '');
      onChange('street2', validatedAddress.street2 || '');
      onChange('unit', validatedAddress.unit || '');
      onChange('city', validatedAddress.city || '');
      onChange('state', validatedAddress.state || '');
      onChange('zip', validatedAddress.zip || '');
      onChange('country', validatedAddress.country || 'US');
      
      if (onValidated && componentValidation) {
        onValidated(componentValidation);
      }
    }
    setShowBanner(false);
    // Reset validation - user must re-validate after using validated address
    setComponentValidation(null);
    setInferredComponents(null);
    setValidatedAddress(null);
  };

  const handleDismissBanner = () => {
    setShowBanner(false);
    setValidatedAddress(null);
    // Reset validation - user must re-validate after dismissing
    setComponentValidation(null);
    setInferredComponents(null);
  };

  // Get validation level for a field
  const getFieldValidationLevel = (field: 'street1' | 'unit' | 'city' | 'state' | 'zip' | 'country'): ComponentValidationLevel | undefined => {
    if (!componentValidation) return undefined;
    
    // For street1, take the lowest value between street_number and route
    if (field === 'street1') {
      const streetNumber = componentValidation.street_number;
      const route = componentValidation.route;
      if (streetNumber !== undefined && route !== undefined) {
        return Math.min(streetNumber, route) as ComponentValidationLevel;
      }
      return streetNumber !== undefined ? streetNumber : route;
    }
    
    // Map other fields
    const fieldMap: Record<string, keyof ComponentValidation> = {
      'unit': 'subpremise',
      'city': 'neighborhood',
      'state': 'administrative_area_level_1',
      'zip': 'postal_code',
      'country': 'country'
    };
    
    return componentValidation[fieldMap[field]];
  };

  // Render validation checkmark
  const renderValidationCheckmark = (field: 'street1' | 'unit' | 'city' | 'state' | 'zip' | 'country') => {
    const level = getFieldValidationLevel(field);
    if (level === undefined || level === ComponentValidationLevel.NEVER_VALIDATED) {
      return null;
    }
    
    let color = '';
    switch (level) {
      case ComponentValidationLevel.CONFIRMED:
        color = '#22c55e'; // Green
        break;
      case ComponentValidationLevel.UNCONFIRMED_BUT_PLAUSIBLE:
        color = '#eab308'; // Yellow
        break;
      case ComponentValidationLevel.UNCONFIRMED_BUT_SUSPICIOUS:
        color = '#f97316'; // Orange
        break;
      case ComponentValidationLevel.CONFIRMATION_LEVEL_UNSPECIFIED:
        color = '#dc2626'; // Red
        break;
    }
    
    return (
      <span className="validation-checkmark" style={{ color }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="9" stroke={color} strokeWidth="2" fill="none"/>
          <path d="M6 10 L9 13 L14 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </span>
    );
  };

  const formatInferredAddress = (
    addr: Address, 
    validation?: ComponentValidation | null,
    inferred?: InferredComponents | null
  ): string => {
    const parts: string[] = [];
    
    // Include components that are CONFIRMED OR inferred
    if (validation) {
      // street1 requires both street_number and route to be CONFIRMED or inferred
      const streetNumberValid = validation.street_number === ComponentValidationLevel.CONFIRMED || inferred?.street_number === true;
      const routeValid = validation.route === ComponentValidationLevel.CONFIRMED || inferred?.route === true;
      if (streetNumberValid && routeValid && addr.street1) {
        parts.push(addr.street1);
      }
      
      // street2 is not validated by Google, so don't include it
      
      // unit (subpremise) must be CONFIRMED or inferred
      const unitValid = validation.subpremise === ComponentValidationLevel.CONFIRMED || inferred?.subpremise === true;
      if (unitValid && addr.unit) {
        parts.push(`Unit ${addr.unit}`);
      }
      
      // city (neighborhood) must be CONFIRMED or inferred
      const cityValid = validation.neighborhood === ComponentValidationLevel.CONFIRMED || inferred?.neighborhood === true;
      if (cityValid && addr.city) {
        parts.push(addr.city);
      }
      
      // state (administrative_area_level_1) must be CONFIRMED or inferred
      const stateValid = validation.administrative_area_level_1 === ComponentValidationLevel.CONFIRMED || inferred?.administrative_area_level_1 === true;
      if (stateValid && addr.state) {
        parts.push(addr.state);
      }
      
      // zip (postal_code) must be CONFIRMED or inferred
      const zipValid = validation.postal_code === ComponentValidationLevel.CONFIRMED || inferred?.postal_code === true;
      if (zipValid && addr.zip) {
        parts.push(addr.zip);
      }
      
      // country must be CONFIRMED or inferred
      const countryValid = validation.country === ComponentValidationLevel.CONFIRMED || inferred?.country === true;
      if (countryValid && addr.country && addr.country !== 'US') {
        parts.push(addr.country);
      }
    } else {
      // If no validation provided, show all components (fallback)
      if (addr.street1) parts.push(addr.street1);
      if (addr.street2) parts.push(addr.street2);
      if (addr.unit) parts.push(`Unit ${addr.unit}`);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.zip) parts.push(addr.zip);
      if (addr.country && addr.country !== 'US') parts.push(addr.country);
    }
    
    return parts.join(', ');
  };

  return (
    <div className="form-section-content">
      {/* Validated Address Banner */}
      {showBanner && validatedAddress && (
        <div className="inferred-address-banner">
          <div className="inferred-address-content">
            <div className="inferred-address-label">Validated address:</div>
            <div className="inferred-address-text">{formatInferredAddress(validatedAddress, componentValidation, inferredComponents)}</div>
          </div>
          <div className="inferred-address-actions">
            <button
              type="button"
              className="app-btn app-btn-primary app-btn-small"
              onClick={handleUseValidatedAddress}
            >
              Use
            </button>
            <button
              type="button"
              className="app-btn app-btn-secondary app-btn-small"
              onClick={handleDismissBanner}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div className="app-form-group">
        <div className="address-field-with-verify">
          <div className="address-field-wrapper">
            <label htmlFor="street1" className="app-form-label required">
              Street Address
              {verified && (
                <span className="app-verified-indicator" title="Verified address">
                  ✓
                </span>
              )}
            </label>
            <div className="input-with-checkmark">
              <input
                id="street1"
                type="text"
                className={`app-form-input ${errors['address.street1'] ? 'error' : ''}`}
                value={addressData.street1}
                onChange={(e) => handleAddressChange('street1', e.target.value)}
                placeholder="123 Main Street"
                maxLength={200}
              />
              {renderValidationCheckmark('street1')}
            </div>
            {errors['address.street1'] && <div className="app-form-error">{errors['address.street1']}</div>}
          </div>
          <button
            type="button"
            className={`app-btn-verify ${verified ? 'verified' : ''}`}
            onClick={handleVerifyAddress}
            disabled={isValidating || verified}
            title={verified ? 'Address is verified' : 'Verify address with Google Maps'}
          >
            ✓
          </button>
        </div>
      </div>

      <div className="app-form-row">
        <div className="app-form-group">
          <label htmlFor="street2" className="app-form-label">
            Street Address 2
          </label>
          <input
            id="street2"
            type="text"
            className="app-form-input"
            value={addressData.street2}
            onChange={(e) => handleAddressChange('street2', e.target.value)}
            placeholder="Apt, Suite, etc. (optional)"
            maxLength={200}
          />
        </div>

        <div className="app-form-group">
          <label htmlFor="unit" className="app-form-label">
            Unit
          </label>
          <div className="input-with-checkmark">
            <input
              id="unit"
              type="text"
              className="app-form-input"
              value={addressData.unit}
              onChange={(e) => handleAddressChange('unit', e.target.value)}
              placeholder="Unit # (optional)"
              maxLength={200}
            />
            {renderValidationCheckmark('unit')}
          </div>
        </div>
      </div>

      <div className="app-form-row">
        <div className="app-form-group">
          <label htmlFor="city" className="app-form-label required">
            City
          </label>
          <div className="input-with-checkmark">
            <input
              id="city"
              type="text"
              className={`app-form-input ${errors['address.city'] ? 'error' : ''}`}
              value={addressData.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              placeholder="City"
              maxLength={50}
            />
            {renderValidationCheckmark('city')}
          </div>
          {errors['address.city'] && <div className="app-form-error">{errors['address.city']}</div>}
        </div>

        <div className="app-form-group">
          <label htmlFor="state" className="app-form-label required">
            State
          </label>
          <div className="input-with-checkmark">
            <input
              id="state"
              type="text"
              className={`app-form-input ${errors['address.state'] ? 'error' : ''}`}
              value={addressData.state}
              onChange={(e) => handleAddressChange('state', e.target.value)}
              placeholder="State"
              maxLength={2}
            />
            {renderValidationCheckmark('state')}
          </div>
          {errors['address.state'] && <div className="app-form-error">{errors['address.state']}</div>}
        </div>

        <div className="app-form-group">
          <label htmlFor="zip" className="app-form-label required">
            Zip Code
          </label>
          <div className="input-with-checkmark">
            <input
              id="zip"
              type="text"
              className={`app-form-input ${errors['address.zip'] ? 'error' : ''}`}
              value={addressData.zip}
              onChange={(e) => handleAddressChange('zip', e.target.value)}
              placeholder="12345"
              maxLength={10}
            />
            {renderValidationCheckmark('zip')}
          </div>
          {errors['address.zip'] && <div className="app-form-error">{errors['address.zip']}</div>}
        </div>
      </div>

      <div className="app-form-group">
        <label htmlFor="country" className="app-form-label required">
          Country
        </label>
        <div className="input-with-checkmark">
          <input
            id="country"
            type="text"
            className={`app-form-input ${errors['address.country'] ? 'error' : ''}`}
            value={addressData.country}
            onChange={(e) => handleAddressChange('country', e.target.value)}
            placeholder="Country (default: US)"
            maxLength={3}
          />
          {renderValidationCheckmark('country')}
        </div>
        {errors['address.country'] && <div className="app-form-error">{errors['address.country']}</div>}
      </div>
    </div>
  );
};

export default AddressFormSection;

